import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { UserRolesEnum } from '@shared/constants/auth.constants';
import {
  CampaignDto,
  useCampaignUpdateControllerCampaignUpdateMutation,
} from '@shared/services/apiService/apiService';
import type { UserRoles } from '@shared/services/apiService/apiService';
import { CampaignRunStatusType } from '@shared/services/apiService/missing-types';

import { mapCampaignAmbienceDraftToRequest } from '../utils/ambienceSettings.utils';
import {
  resolveAmbienceSettingsErrorMessage,
  showAmbienceSettingsErrorToast,
  showAmbienceSettingsSuccessToast,
} from '../utils/ambienceSettingsToast.utils';
import { useAmbienceSettingsController } from './useAmbienceSettingsController';

interface UseCampaignAmbienceSettingsOptions {
  campaign?: CampaignDto;
  currentUserRole?: UserRoles;
  refetch: () => Promise<unknown>;
}

export const useCampaignAmbienceSettings = ({
  campaign,
  currentUserRole,
  refetch,
}: UseCampaignAmbienceSettingsOptions) => {
  const { t } = useTranslation();
  const [updateCampaign, { isLoading: isSaving }] =
    useCampaignUpdateControllerCampaignUpdateMutation();
  const isReadOnly =
    currentUserRole === UserRolesEnum.VIEWER || campaign?.status !== CampaignRunStatusType.INACTIVE;
  const disableReason =
    campaign?.status !== CampaignRunStatusType.INACTIVE
      ? t('campaignPage.ambienceSettings.disabledCampaign')
      : null;
  const controller = useAmbienceSettingsController({
    mode: 'campaign',
    backgroundSounds: campaign?.backgroundSounds,
    isReadOnly,
  });
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);

  useEffect(() => {
    if (!controller.hasValidationError && !controller.hasMissingSounds) {
      setHasAttemptedSave(false);
    }
  }, [controller.hasMissingSounds, controller.hasValidationError]);

  const save = useCallback(async () => {
    if (!campaign?.id || !controller.draft || isReadOnly) {
      return false;
    }

    if (controller.hasValidationError || controller.hasMissingSounds) {
      setHasAttemptedSave(true);
      return false;
    }

    try {
      await updateCampaign({
        id: campaign.id,
        campaignUpdateRequestDto: {
          backgroundSounds: mapCampaignAmbienceDraftToRequest(controller.draft),
        },
      }).unwrap();
      setHasAttemptedSave(false);
      showAmbienceSettingsSuccessToast({
        title: t('common.saved'),
        description: t('campaignPage.ambienceSettings.saveSuccessDescription'),
      });
      await refetch();
      return true;
    } catch (error) {
      showAmbienceSettingsErrorToast(
        resolveAmbienceSettingsErrorMessage(
          error,
          t('campaignPage.ambienceSettings.saveErrorDescription'),
        ),
      );
      return false;
    }
  }, [campaign?.id, controller, isReadOnly, refetch, t, updateCampaign]);

  const cancel = useCallback(() => {
    setHasAttemptedSave(false);
    controller.resetDraft();
  }, [controller]);

  return {
    ...controller,
    isReadOnly,
    disableReason,
    hasAttemptedSave,
    isSaving,
    save,
    cancel,
  };
};
